import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useBlogStore } from '@/store/blogStore';
import  ArticleCard  from '@/components/blog/ArticleCard';
import { TopicSidebar } from '@/components/blog/topic/TopicSidebar';
import { EmptyTopicState } from '@/components/blog/topic/EmptyTopicState';
import  Breadcrumb  from '@/components/blog/Breadcrumb';
// Removed unused import SEOHead
import { 
  AdjustmentsHorizontalIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { SortOption } from '@/types/blog';

type ViewMode = 'grid' | 'list';

export const TopicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('oldest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const {
    currentTopic,
    posts,
    loading,
    topicsLoading,
    error,
    pagination,
    fetchTopicBySlug,
    fetchPostsByTopic,
    clearError
  } = useBlogStore();

  // Get current page from URL params
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    if (!slug) return;

    const loadTopicData = async () => {
      try {
        // Fetch topic details and posts in parallel
        await Promise.all([
          fetchTopicBySlug(slug),
          fetchPostsByTopic(slug, { 
            page: currentPage,
            limit: 12,
            sort: sortBy 
          })
        ]);
      } catch (error) {
        console.error('Error loading topic data:', error);
      }
    };

    loadTopicData();
  }, [slug, currentPage, sortBy, fetchTopicBySlug, fetchPostsByTopic]);

  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
    setShowSortDropdown(false);
    // Reset to page 1 when changing sort
    setSearchParams({ sort: newSort });
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Loading state
  if (topicsLoading && !currentTopic) {
    return (
      <div className="min-h-screen bg-m2-surface-low">
      
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-m2-surface-highest rounded w-1/4 mb-6"></div>
            <div className="h-8 bg-m2-surface-highest rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-m2-surface-highest rounded w-3/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-48 bg-m2-surface-highest rounded"></div>
                ))}
              </div>
              <div className="h-96 bg-m2-surface-highest rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !currentTopic) {
    return (
      <div className="min-h-screen bg-m2-surface-low flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-m2-error/10 border border-m2-error/30 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-m2-error mb-2">
              Topic Not Found
            </h2>
            <p className="text-red-600 mb-4">
              {error}
            </p>
            <button
              onClick={() => window.history.back()}
              className="eh-btn text-sm"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTopic) {
    return null;
  }

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: currentTopic.title }
  ];

  // Sort options
  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'oldest', label: 'Oldest First' },
    { value: 'newest', label: 'Newest First' },
    { value: 'most_viewed', label: 'Most Viewed' }
  ];

  const selectedSortLabel = sortOptions.find(option => option.value === sortBy)?.label || 'Oldest First';

  return (
    <div className="min-h-screen bg-m2-background">
      <div className="eh-container py-10">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        {/* Topic Header with Article Count */}
         {/* 
        <div className="bg-m2-surface-container rounded-lg shadow-sm p-8 mb-8">
          <div className="text-center">
            <div className="text-2xl font-bold text-m2-on-surface mb-2">
              Bài viết trong chủ đề này ({currentTopic.articleCount})
            </div>
            
            {currentTopic.description && (
              <p className="text-lg text-m2-on-surface-variant leading-relaxed max-w-3xl mx-auto">
                {currentTopic.description}
              </p>
            )}
          </div>
        </div>
        */}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Articles Section */}
          <div className="lg:col-span-3">
            {/* Controls */}
            <div className="bg-m2-surface-lowest rounded-eh border border-eh-border shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Empty space for alignment */}
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* Sort Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowSortDropdown(!showSortDropdown)}
                      className="inline-flex items-center px-3 py-2 border border-eh-border rounded-eh-sm text-sm font-medium text-m2-on-surface bg-m2-surface-lowest hover:bg-eh-keylime focus:outline-none focus:ring-2 focus:ring-eh-forest-ink/30 focus:ring-offset-2"
                    >
                      <AdjustmentsHorizontalIcon className="w-4 h-4 mr-2" />
                      {selectedSortLabel}
                      <ChevronDownIcon className="w-4 h-4 ml-2" />
                    </button>
                    
                    {showSortDropdown && (
                      <div className="absolute right-0 z-10 mt-2 w-48 bg-m2-surface-container border border-m2-outline rounded-md shadow-lg">
                        <div className="py-1">
                          {sortOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleSortChange(option.value)}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-m2-surface-low ${
                                sortBy === option.value ? 'bg-eh-sage text-eh-forest-ink' : 'text-m2-on-surface'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* View Mode Toggle */}
                  <div className="flex border border-m2-outline rounded-md">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${
                        viewMode === 'grid' 
                          ? 'bg-eh-sage text-eh-forest-ink' 
                          : 'text-m2-outline hover:text-m2-on-surface-variant'
                      }`}
                    >
                      <Squares2X2Icon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${
                        viewMode === 'list' 
                          ? 'bg-eh-sage text-eh-forest-ink' 
                          : 'text-m2-outline hover:text-m2-on-surface-variant'
                      }`}
                    >
                      <ListBulletIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Articles Grid */}
            {loading ? (
              <div className="space-y-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-48 bg-m2-surface-highest rounded animate-pulse"></div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <EmptyTopicState topic={currentTopic} />
            ) : (
              <>
                <div className={
                  viewMode === 'grid' 
                    ? 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'
                    : 'space-y-6 mb-8'
                }>
                  {posts.map((post) => (
                    <ArticleCard 
                      key={post.uuid} 
                      post={post}
                      className={viewMode === 'list' ? 'flex' : ''}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-4 py-2 text-sm font-medium text-m2-on-surface bg-m2-surface-container border border-m2-outline rounded-md hover:bg-m2-surface-low disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          page === pagination.currentPage
                            ? 'bg-eh-forest-ink text-eh-cream'
                            : 'text-m2-on-surface bg-m2-surface-container border border-m2-outline hover:bg-m2-surface-low'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-4 py-2 text-sm font-medium text-m2-on-surface bg-m2-surface-container border border-m2-outline rounded-md hover:bg-m2-surface-low disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <TopicSidebar 
              posts={posts}
              topicSlug={currentTopic.slug}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicPage;